import { useState } from "react";
import { Link } from "react-router-dom";
import { AddressAutocomplete } from "@/components/lembra/AddressAutocomplete";
import { Button } from "@/components/ui/button";
import { formatAud, type ThreadWidget, type WidgetActionRequest } from "@/lib/threadWidgets";

export interface ThreadWidgetStripProps {
  widgets: ThreadWidget[];
  onWidgetAction: (action: WidgetActionRequest) => void;
  onSendMessage: (message: string) => void;
  disabled?: boolean;
}

export function ThreadWidgetStrip({
  widgets,
  onWidgetAction,
  onSendMessage,
  disabled = false,
}: ThreadWidgetStripProps) {
  if (widgets.length === 0) return null;

  return (
    <div
      className="flex flex-col gap-2.5 rounded-xl border border-navy/10 bg-white/90 p-3"
      data-testid="thread-widget-strip"
    >
      {widgets.map((widget) => (
        <ThreadWidgetRow
          key={widget.type === "team_member_highlight"
            ? `tm-${widget.team_member_id}`
            : "id" in widget ? widget.id : widget.type}
          widget={widget}
          onWidgetAction={onWidgetAction}
          onSendMessage={onSendMessage}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function ThreadWidgetRow({
  widget,
  onWidgetAction,
  onSendMessage,
  disabled,
}: {
  widget: ThreadWidget;
  onWidgetAction: (action: WidgetActionRequest) => void;
  onSendMessage: (message: string) => void;
  disabled: boolean;
}) {
  const [addressDraft, setAddressDraft] = useState("");

  switch (widget.type) {
    case "confirm_action":
      return (
        <div className="flex flex-col gap-2">
          {widget.description && (
            <p className="text-xs text-charcoal/65">{widget.description}</p>
          )}
          <Button
            type="button"
            size="sm"
            variant={widget.variant === "destructive" ? "destructive" : "primary"}
            disabled={disabled}
            onClick={() => onWidgetAction({ tool: widget.tool, input: widget.input })}
          >
            {widget.label}
          </Button>
        </div>
      );

    case "undo_action":
      return (
        <div className="flex items-center justify-between gap-2">
          {widget.description && (
            <p className="text-xs text-charcoal/65">{widget.description}</p>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled}
            onClick={() => onWidgetAction({ tool: widget.tool, input: widget.input })}
          >
            {widget.label}
          </Button>
        </div>
      );

    case "address_capture":
      return (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium text-navy">{widget.label}</p>
          <AddressAutocomplete
            value={addressDraft}
            onChange={(e) => setAddressDraft(e.target.value)}
            placeholder={widget.placeholder ?? "Start typing an address…"}
            disabled={disabled}
          />
          <Button
            type="button"
            size="sm"
            variant="primary"
            disabled={disabled || !addressDraft.trim()}
            onClick={() => {
              onWidgetAction({
                tool: "save_delivery_address",
                input: {},
                address: addressDraft.trim(),
              });
              onSendMessage(`Use this delivery address: ${addressDraft.trim()}`);
            }}
          >
            {widget.submit_label ?? "Use this address"}
          </Button>
        </div>
      );

    case "vendor_picker":
      return (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium text-navy">{widget.label}</p>
          <div className="flex flex-wrap gap-1.5">
            {widget.vendors.map((v) => (
              <button
                key={v.vendor_partner_id}
                type="button"
                disabled={disabled}
                onClick={() =>
                  onSendMessage(`Show me gift options from ${v.vendor_name}`)
                }
                className="rounded-full border border-navy/15 bg-cream/50 px-3 py-1.5 text-xs font-medium text-navy transition-colors hover:border-coral hover:text-coral disabled:opacity-50"
              >
                {v.vendor_name}
              </button>
            ))}
          </div>
        </div>
      );

    case "celebration_picker":
      return (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium text-navy">{widget.label}</p>
          <div className="flex flex-col gap-1">
            {widget.celebrations.map((c) => (
              <button
                key={c.celebration_id}
                type="button"
                disabled={disabled}
                onClick={() =>
                  onWidgetAction({
                    tool: "skip_celebration",
                    input: { celebration_id: c.celebration_id },
                  })
                }
                className="rounded-lg border border-navy/10 px-3 py-2 text-left text-sm text-navy hover:border-coral/40 hover:bg-coral/5 disabled:opacity-50"
              >
                {c.label || `${c.type} · ${c.date}`}
              </button>
            ))}
          </div>
        </div>
      );

    case "budget_summary":
      return (
        <div
          className="rounded-lg bg-butter/30 px-3 py-2.5"
          data-testid="budget-summary-widget"
        >
          <p className="text-[11px] font-semibold uppercase tracking-wide text-charcoal/55">
            Budget · {widget.period_label}
          </p>
          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-navy">
            <span className="text-charcoal/65">Pool</span>
            <span className="font-medium">{formatAud(widget.allocated_aud)}</span>
            <span className="text-charcoal/65">Spent</span>
            <span>{formatAud(widget.spent_aud)}</span>
            <span className="text-charcoal/65">Scheduled</span>
            <span>{formatAud(widget.scheduled_aud)}</span>
            <span className="text-charcoal/65">Remaining</span>
            <span className="font-semibold text-navy">{formatAud(widget.remaining_aud)}</span>
          </div>
          <Link
            to="/settings/program#budget"
            className="mt-2 inline-block text-xs font-medium text-coral underline underline-offset-2"
          >
            Adjust in Settings
          </Link>
        </div>
      );

    case "team_member_highlight":
      return (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-navy">
            {widget.message ?? `Updated ${widget.name}`}
          </p>
          <Link
            to={`/team/${widget.team_member_id}/profile`}
            className="text-xs font-semibold text-coral underline underline-offset-2"
          >
            View profile
          </Link>
        </div>
      );

    default:
      return null;
  }
}
